/**
 * TimingTableV2 Component
 * Checkpoint timing table for runner detail page
 *
 * Desktop: Full table with columns: CHECKPOINT | DIST | ELEV | CHECK IN | CHECK OUT | IN AID | SEG TIME | PACE | STATUS
 * Mobile: Card-based layout per checkpoint
 */

import type { SplitTime, AidStation } from '../../lib/types';
import { formatTimeInZone } from '../../lib/timeFormatter';

interface TimingTableV2Props {
  splitTimes: SplitTime[];
  stations: AidStation[];
  raceTimeZone: string;
  raceStartTime: string;
}

function formatElapsed(ms: number | null): string {
  if (ms == null) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

type TimingStatus = 'passed' | 'finished' | 'next' | 'upcoming';

function getTimingStatus(split: SplitTime, lastPassedIndex: number, index: number, isFinish: boolean): TimingStatus {
  // A finish-line split only has a time once the runner has finished, so badge it FINISHED
  // (matching the runner's overall status) rather than the generic PASSED.
  if (split.time) return isFinish ? 'finished' : 'passed';
  if (index === lastPassedIndex + 1) return 'next';
  return 'upcoming';
}

function timingBadgeLabel(status: TimingStatus): string {
  switch (status) {
    case 'passed': return 'THROUGH';
    case 'finished': return 'FINISHED';
    case 'next': return 'NEXT';
    default: return '—';
  }
}

export function TimingTableV2({ splitTimes, stations, raceTimeZone, raceStartTime: _raceStartTime }: TimingTableV2Props) {
  void _raceStartTime; // Available for future absolute time calculations
  // Station ids that represent the finish line — used to badge that row FINISHED instead of PASSED.
  const finishStationIds = new Set(
    stations.filter((s) => s.check_mode === 'finish').map((s) => s.id)
  );
  // Find the last passed checkpoint index
  const lastPassedIndex = splitTimes.reduce((lastIdx, split, idx) => {
    return split.time ? idx : lastIdx;
  }, -1);

  // Calculate segment times (time between consecutive checkpoints)
  const segmentTimes: (number | null)[] = splitTimes.map((split, i) => {
    if (!split.elapsed_time) return null;
    if (i === 0) return split.elapsed_time;
    const prevElapsed = splitTimes[i - 1]?.elapsed_time;
    if (prevElapsed == null) return null;
    return split.elapsed_time - prevElapsed;
  });

  // Calculate pace per segment (min/mile)
  const segmentPaces: (string | null)[] = splitTimes.map((split, i) => {
    const segTime = segmentTimes[i];
    if (segTime == null) return null;
    const prevMileage = i > 0 ? (splitTimes[i - 1]?.station_mileage ?? 0) : 0;
    const segDistance = split.station_mileage - prevMileage;
    if (segDistance <= 0) return null;
    const paceMinutes = (segTime / 1000 / 60) / segDistance;
    const baseMins = Math.floor(paceMinutes);
    const roundedSecs = Math.round((paceMinutes - baseMins) * 60);
    // Carry into the minute field when rounding pushes seconds to 60 (e.g. 12.999 -> 13:00).
    const mins = roundedSecs === 60 ? baseMins + 1 : baseMins;
    const secs = roundedSecs === 60 ? 0 : roundedSecs;
    return `${mins}:${String(secs).padStart(2, '0')}/mi`;
  });

  if (splitTimes.length === 0) {
    return (
      <div className="v2-timing-section">
        <div className="v2-timing-title">TIMING POINTS</div>
        <p style={{ color: 'var(--v2-text-muted)', fontSize: 13 }}>
          No timing data available yet.
        </p>
      </div>
    );
  }

  return (
    <div className="v2-timing-section">
      <div className="v2-timing-title">TIMING POINTS</div>

      {/* Desktop table */}
      <div className="v2-runner-table-desktop">
        <table className="v2-timing-table">
          <thead>
            <tr>
              <th>CHECKPOINT</th>
              <th>DIST</th>
              <th>CHECK IN</th>
              <th>ELAPSED</th>
              <th>SEG TIME</th>
              <th>PACE</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {splitTimes.map((split, i) => {
              const status = getTimingStatus(split, lastPassedIndex, i, finishStationIds.has(split.station_id));
              return (
                <tr key={split.station_id}>
                  <td className="v2-timing-station">{split.station_name}</td>
                  <td className="v2-mono" style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>
                    {split.station_mileage > 0 ? `${split.station_mileage.toFixed(1)} mi` : '—'}
                  </td>
                  <td className="v2-mono">
                    {split.time
                      ? formatTimeInZone(split.time, raceTimeZone)
                      : '—'}
                  </td>
                  <td className="v2-mono">
                    {formatElapsed(split.elapsed_time)}
                  </td>
                  <td className="v2-mono">
                    {segmentTimes[i] != null ? formatElapsed(segmentTimes[i]!) : '—'}
                  </td>
                  <td className="v2-mono" style={{ fontSize: 11 }}>
                    {segmentPaces[i] || '—'}
                  </td>
                  <td>
                    <span className={`v2-timing-badge--${status}`}>
                      {timingBadgeLabel(status)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="v2-runner-mobile-list">
        {splitTimes.map((split, i) => {
          const status = getTimingStatus(split, lastPassedIndex, i, finishStationIds.has(split.station_id));
          return (
            <div className="v2-timing-card" key={split.station_id}>
              <div className="v2-timing-card-header">
                <span className="v2-timing-card-station">{split.station_name}</span>
                <span className={`v2-timing-badge--${status}`}>
                  {timingBadgeLabel(status)}
                </span>
              </div>
              <div className="v2-timing-card-meta">
                {split.station_mileage > 0 ? `${split.station_mileage.toFixed(1)} mi` : ''}
              </div>
              {split.time && (
                <div className="v2-timing-card-grid">
                  <div className="v2-timing-card-item">
                    <span className="v2-timing-card-label">IN</span>
                    <span className="v2-timing-card-value">
                      {formatTimeInZone(split.time, raceTimeZone)}
                    </span>
                  </div>
                  <div className="v2-timing-card-item">
                    <span className="v2-timing-card-label">ELAPSED</span>
                    <span className="v2-timing-card-value">
                      {formatElapsed(split.elapsed_time)}
                    </span>
                  </div>
                  <div className="v2-timing-card-item">
                    <span className="v2-timing-card-label">PACE</span>
                    <span className="v2-timing-card-value">
                      {segmentPaces[i] || '—'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
